import React, { useRef, useCallback, useState, Suspense } from "react"
import { useGesture } from "react-use-gesture"
import { useSprings, a } from "react-spring"
import DragTooltipDesktop from "../Tooltips/DragTooltipDesktop"
import Loader from "../Loader"
import styled from "styled-components"

const styles = {
  container: { position: "relative", height: "100%", width: "100%" },
  item: { position: "absolute", height: "100%", willChange: "transform" }
}

const TooltipPivot = styled.div`
  position: absolute;
  top: 85%;
  left: 50%;
  z-index: 1;
  transform: translate(-50%, -50%);
`

/**
 * Calculates a spring-physics driven infinite slider
 * @param {Array} items - display items
 * @param {Function} children - render child
 * @param {number} width - fixed item with
 * @param {number} visible - number of items that muste be visible on screen
 */
export default function Gallery2D({
  items,
  width = 600,
  visible = 4,
  style,
  children
}) {
  const idx = useCallback(
    (x, l = items.length) => (x < 0 ? x + l : x) % l,
    [items]
  )
  const getPos = useCallback(
    (i, firstVis, firstVisIdx) => idx(i - firstVis + firstVisIdx),
    [idx]
  )
  const [springs, api] = useSprings(items.length, i => ({
    x: (i < items.length - 1 ? i : -1) * width
  }))
  const prev = useRef([0, 1])

  const runSprings = useCallback(
    (y, vy) => {
      const firstVis = idx(Math.floor(y / width) % items.length)
      const firstVisIdx = vy < 0 ? items.length - visible - 1 : 1
      api.start(i => {
        const position = getPos(i, firstVis, firstVisIdx)
        const prevPosition = getPos(i, prev.current[0], prev.current[1])
        const rank =
          firstVis - (y < 0 ? items.length : 0) + position - firstVisIdx
        const configPos = vy > 0 ? position : items.length - position
        return {
          x: (-y % (width * items.length)) + width * rank,
          immediate: vy < 0 ? prevPosition > position : prevPosition < position,
          config: {
            tension: (1 + items.length - configPos) * 100,
            friction: 30 + configPos * 40
          }
        }
      })
      prev.current = [firstVis, firstVisIdx]
    },
    [idx, getPos, width, visible, api, items.length]
  )

  const wheelOffset = useRef(0)
  const dragOffset = useRef(0)

  const [tooltipVisible, setTooltipVisible] = useState(true)

  const handleDrag = state => {
    const {
      offset: [x],
      vxvy: [vx]
    } = state
    if (vx) {
      dragOffset.current = -x
      runSprings(wheelOffset.current + -x, -vx)
    }

    //Hide tooltip
    if (tooltipVisible) {
      setTooltipVisible(false)
    }
  }

  const handleWheel = state => {
    const {
      offset: [, y],
      vxvy: [, vy]
    } = state
    if (vy) {
      wheelOffset.current = y
      runSprings(dragOffset.current + y, vy)
    }

    //Hide tooltip
    if (tooltipVisible) {
      setTooltipVisible(false)
    }
  }

  const bind = useGesture({
    onDrag: state => handleDrag(state),
    onWheel: state => handleWheel(state)
  })

  return (
    <Suspense fallback={<Loader />}>
      <div {...bind()} style={{ ...style, ...styles.container }}>
        <TooltipPivot>
          <DragTooltipDesktop isVisible={tooltipVisible}></DragTooltipDesktop>
        </TooltipPivot>
        {springs.map(({ x, vel }, i) => (
          <a.div
            key={i}
            style={{
              ...styles.item,
              width,
              x
              // backgroundColor: items[i].bgColor
            }}
            children={children(items[i], i)}
          />
        ))}
      </div>
    </Suspense>
  )
}
